import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IoArrowBack, IoInformationCircle, IoWarning, IoChatbubble, IoLogoApple } from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import {
  requestPhoneOtp,
  verifyPhoneOtp,
  lookupUserByVerifiedPhone,
} from '../../../services/authService';

const PROVIDER_META = {
  kakao: { label: '카카오' },
  google: { label: '구글' },
  apple: { label: '애플' },
};

const ProviderBadge = ({ provider }) => {
  if (provider === 'kakao') return <IoChatbubble size={14} color="#000" />;
  if (provider === 'apple') return <IoLogoApple size={14} color="#000" />;
  return <FcGoogle size={14} />;
};

const OTP_LENGTH = 6;
const OTP_TIMEOUT_SEC = 3 * 60; // 3분 타이머

const LinkPhonePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { authUid, provider } = state;

  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState(Array(OTP_LENGTH).fill(''));
  const [remainSec, setRemainSec] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [testCode, setTestCode] = useState('');
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!authUid || !provider) {
      navigate('/login', { replace: true });
    }
  }, [authUid, provider, navigate]);

  // 타이머
  useEffect(() => {
    if (!sent || remainSec <= 0) return;
    const t = setInterval(() => setRemainSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [sent, remainSec]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const formatPhoneDisplay = (raw) => {
    const d = raw.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
    if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneDisplay(e.target.value));
    setErrMsg('');
  };

  const handleSendOtp = async () => {
    if (loading) return;
    const norm = phone.replace(/\D/g, '');
    if (norm.length < 10) {
      setErrMsg('전화번호를 정확히 입력해 주세요.');
      return;
    }
    setLoading(true);
    setErrMsg('');
    try {
      const res = await requestPhoneOtp(norm, 'social_link');
      setTestCode(res?.testCode || '');
      setSent(true);
      setCode(Array(OTP_LENGTH).fill(''));
      setRemainSec(OTP_TIMEOUT_SEC);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    } catch (err) {
      setErrMsg(err?.message || '인증번호 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (idx, val) => {
    // 1자리 입력은 해당 칸만, 여러 자리(자동완성/SMS 1회용 코드/복붙 onChange 경유)는 idx부터 우측으로 분배
    const digits = (val || '').replace(/\D/g, '');
    if (!digits) {
      const cleared = [...code];
      cleared[idx] = '';
      setCode(cleared);
      setErrMsg('');
      return;
    }
    const fillLen = Math.min(digits.length, OTP_LENGTH - idx);
    const next = [...code];
    for (let i = 0; i < fillLen; i++) next[idx + i] = digits[i];
    setCode(next);
    setErrMsg('');
    const focusIdx = Math.min(idx + fillLen, OTP_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleCodeKey = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCode(next);
    inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const codeStr = code.join('');
  const canSubmit = sent && codeStr.length === OTP_LENGTH && remainSec > 0 && !loading;

  const handleVerify = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setErrMsg('');
    try {
      const norm = phone.replace(/\D/g, '');
      const { verifiedToken } = await verifyPhoneOtp(norm, codeStr);
      // 조회만 수행 (쓰기는 MergeConfirmPage에서 사용자 확인 후)
      const result = await lookupUserByVerifiedPhone({
        phone: norm,
        verifiedToken,
        socialUid: authUid,
        provider,
      });

      if (result.found) {
        navigate('/social/merge-confirm', {
          state: {
            ...state,
            phone: norm,
            verifiedToken,
            existingUser: result.user,
          },
          replace: true,
        });
      } else {
        navigate('/social/no-account', {
          state: { ...state, phone: norm, verifiedToken },
          replace: true,
        });
      }
    } catch (err) {
      setErrMsg(err?.message || '인증에 실패했습니다.');
      setCode(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (!authUid || !provider) return null;
  const providerMeta = PROVIDER_META[provider] || PROVIDER_META.google;

  return (
    <Wrapper>
      <Card>
        <BackBtn onClick={() => navigate(-1)}>
          <IoArrowBack size={16} />
          <span>뒤로가기</span>
        </BackBtn>

        <ProviderChip>
          <ProviderBadge provider={provider} />
          <span>{providerMeta.label}</span>
        </ProviderChip>

        <Title>휴대폰 인증</Title>
        <SubTitle>기존 계정과 연결하기 위해 휴대폰 번호를 인증해 주세요.</SubTitle>

        <Divider />

        <InfoBox>
          <IoInformationCircle size={18} color="#4A6CF7" />
          <div>
            <strong>1인 1계정 통합 원칙</strong>
            <p>동일한 휴대폰 번호로 가입된 계정이 있을 경우, 소셜 계정이 자동으로 통합됩니다.</p>
          </div>
        </InfoBox>

        <Label>휴대폰 번호</Label>
        <PhoneRow>
          <Input
            type="tel"
            inputMode="numeric"
            placeholder="010-0000-0000"
            value={phone}
            onChange={handlePhoneChange}
            disabled={loading}
          />
          <SendBtn
            type="button"
            onClick={handleSendOtp}
            disabled={loading || phone.replace(/\D/g, '').length < 10}
          >
            {sent ? '재전송' : '인증번호 받기'}
          </SendBtn>
        </PhoneRow>

        {sent && (
          <>
            <CodeHeader>
              <Label>인증번호</Label>
              <Timer $expired={remainSec <= 0}>{formatTime(remainSec)}</Timer>
            </CodeHeader>
            <CodeRow>
              {code.map((v, i) => (
                <CodeInput
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  value={v}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKey(i, e)}
                  onPaste={handleCodePaste}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={6}
                  $error={!!errMsg}
                  disabled={loading || remainSec <= 0}
                />
              ))}
            </CodeRow>
            {testCode && (
              <TestCodeBox>
                <strong>App Review 테스트 코드</strong>
                <code>{testCode}</code>
                <small>이 번호는 심사용 테스트 번호입니다. 위 코드를 입력해 주세요.</small>
              </TestCodeBox>
            )}
            {errMsg && (
              <ErrBox>
                <IoWarning size={16} />
                <span>{errMsg}</span>
              </ErrBox>
            )}
            <ResendHint>
              인증번호가 오지 않나요?{' '}
              <ResendLink onClick={handleSendOtp} disabled={loading}>재전송</ResendLink>
            </ResendHint>
          </>
        )}

        {!sent && errMsg && (
          <ErrBox>
            <IoWarning size={16} />
            <span>{errMsg}</span>
          </ErrBox>
        )}

        <SubmitBtn type="button" disabled={!canSubmit} onClick={handleVerify}>
          {loading ? '확인 중...' : '인증 완료'}
        </SubmitBtn>
      </Card>
    </Wrapper>
  );
};

export default LinkPhonePage;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1024px) {
    align-items: center;
    background: #2f3542;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};

  @media (min-width: 1024px) {
    padding: 40px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  }
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ProviderChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const SubTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const InfoBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 14px 16px;
  background: #EFF4FF;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.primary};

  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    margin-bottom: 4px;
  }

  p {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: #4A6CF7;
    margin: 0;
    line-height: 1.5;
  }
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PhoneRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Input = styled.input`
  flex: 1;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  outline: none;
  background: #fff;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const SendBtn = styled.button`
  padding: 0 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm};
`;

const Timer = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ $expired, theme }) => ($expired ? theme.colors.danger : theme.colors.primary)};
`;

const CodeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CodeInput = styled.input`
  width: 100%;
  aspect-ratio: 1;
  border: 1.5px solid ${({ $error, theme }) => ($error ? theme.colors.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  text-align: center;
  background: #fff;
  outline: none;
  padding: 0;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
  &:disabled { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const ErrBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  background: #FEE2E2;
  color: ${({ theme }) => theme.colors.danger};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TestCodeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 16px;
  background: #FEF3C7;
  border: 1px dashed #F59E0B;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  strong {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: #92400E;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }
  code {
    font-size: 28px;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    color: #111827;
    letter-spacing: 6px;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
  }
  small {
    font-size: 12px;
    color: #92400E;
  }
`;

const ResendHint = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ResendLink = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  padding: 0;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:disabled {
    background: ${({ theme }) => theme.colors.borderLight};
    color: ${({ theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;
